import { DocumentFileType } from '@risksmart-app/domain/src/types/consts/document-file-type';
import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type { UpdateDocumentVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateDocumentVersionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

import {
  toNativeDocumentVersionPayload,
  type UpdateDocumentVersionInput,
} from './documentVersionPayload';
export type { UpdateDocumentVersionInput } from './documentVersionPayload';

export interface UpdateDocumentVersionOptions {
  /**
   * When set, threads the `x-confirm-change-request` and (optional)
   * `x-requester-comment` headers onto the GraphQL mutation context — the
   * legacy publish/CR flow relies on these. The tRPC path handles change
   * requests via the governed confirm/replay flow instead.
   */
  confirmChangeRequest?: boolean;
  requesterComment?: string;
}

const toGraphQLVariables = (input: UpdateDocumentVersionInput) => {
  const shared = {
    Id: input.Id,
    LatestModifiedAtTimestamp: input.LatestModifiedAtTimestamp,
    Status: input.Status,
    Version: input.Version,
    Summary: input.Summary,
    ReasonForReview: input.ReasonForReview,
    ReviewedBy: input.ReviewedBy,
    ReviewDate: input.ReviewDate,
    NextReviewDate: input.NextReviewDate,
    CustomAttributeData: input.CustomAttributeData,
  };
  switch (input.Type) {
    case 'file':
      return {
        ...shared,
        Type: DocumentFileType.File,
        FileId: input.FileId,
        Content: null,
        Link: null,
      };
    case 'html':
      return {
        ...shared,
        Type: DocumentFileType.Html,
        FileId: null,
        Content: input.Content,
        Link: null,
      };
    case 'link':
      return {
        ...shared,
        Type: DocumentFileType.Link,
        FileId: null,
        Content: null,
        Link: input.Link,
      };
  }
};

/**
 * Maps the tRPC response to the GraphQL mutation structure. The legacy
 * GraphQL contract returned `{ affected_rows: number }`; the tRPC response
 * carries `{ Id, ModifiedAtTimestamp }` but consumers only branch on
 * success/error.
 */
const mapTrpcResponseToGraphQL = (_applied: {
  Id: string;
  ModifiedAtTimestamp: string;
}): UpdateDocumentVersionMutation => ({
  updateDocumentVersion: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

export const useUpdateDocumentVersion = createMutationHook({
  name: 'updateDocumentVersion',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.documentFile.update.mutationOptions({ onSuccess }),
  mapInput: toNativeDocumentVersionPayload,
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string; ModifiedAtTimestamp: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
    trpc.frontend.documentFile.latestDocumentFile.queryKey(),
    trpc.frontend.documentFile.documentFileById.queryKey(),
    trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
    trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
    // COI-471 parity: Apollo twin also evicts `document` (version changes
    // affect the parent document's current-version surfaces).
    trpc.frontend.document.pathKey(),
    // COI-889: the ribbon's attestation tile only counts records whose node's
    // document version is Published (`getRibbonAttestationRecords`:
    // `node.documentFile.Status = published`), and this hook writes that
    // Status — publishing or archiving a version moves the count.
    trpc.frontend.myItems.pathKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are
  // success. Echo the input Id + timestamp on the CR branch (the version is
  // unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string; ModifiedAtTimestamp: string }>,
    input: UpdateDocumentVersionInput
  ) =>
    mapTrpcResponseToGraphQL(
      unwrapApprovalGated(result, {
        Id: input.Id,
        ModifiedAtTimestamp: input.LatestModifiedAtTimestamp,
      })
    ),
  graphqlDocument: UpdateDocumentVersionDocument,
  graphqlVariables: (input: UpdateDocumentVersionInput) => ({
    ...toGraphQLVariables(input),
  }),
  evictFields: [
    'document',
    'document_file',
    'document_file_by_pk',
    'change_request',
  ],
  refetchQueries: [namedOperations.Query.getDocumentFileById],
  apolloContext: (options: UpdateDocumentVersionOptions | undefined) => ({
    headers: {
      'x-confirm-change-request': options?.confirmChangeRequest
        ? 'true'
        : 'false',
      ...(options?.requesterComment
        ? {
            'x-requester-comment': encodeURIComponent(options.requesterComment),
          }
        : {}),
    },
  }),
});
